import { useMemo, useState } from 'react';
import { ArrowRight, TrendDown, Package, Scales } from '@phosphor-icons/react';

const PRICING = {
  '<1lb': { '0-20': { '1k-3k': 4.15, '3k-5k': 3.85, '5k-10k': 3.55, '10k+': 3.25 }, '20-50': { '1k-3k': 4.35, '3k-5k': 4.05, '5k-10k': 3.75, '10k+': 3.45 }, '50-100': { '1k-3k': 4.55, '3k-5k': 4.25, '5k-10k': 3.95, '10k+': 3.65 } },
  '1-3lb': { '0-20': { '1k-3k': 4.75, '3k-5k': 4.45, '5k-10k': 4.15, '10k+': 3.85 }, '20-50': { '1k-3k': 4.95, '3k-5k': 4.65, '5k-10k': 4.35, '10k+': 4.05 }, '50-100': { '1k-3k': 5.15, '3k-5k': 4.85, '5k-10k': 4.55, '10k+': 4.25 } },
  '3-5lb': { '0-20': { '1k-3k': 5.35, '3k-5k': 5.05, '5k-10k': 4.75, '10k+': 4.45 }, '20-50': { '1k-3k': 5.55, '3k-5k': 5.25, '5k-10k': 4.95, '10k+': 4.65 }, '50-100': { '1k-3k': 5.75, '3k-5k': 5.45, '5k-10k': 5.15, '10k+': 4.85 } },
  '5-7lb': { '0-20': { '1k-3k': 6.15, '3k-5k': 5.85, '5k-10k': 5.55, '10k+': 5.25 }, '20-50': { '1k-3k': 6.35, '3k-5k': 6.05, '5k-10k': 5.75, '10k+': 5.45 }, '50-100': { '1k-3k': 6.55, '3k-5k': 6.25, '5k-10k': 5.95, '10k+': 5.65 } },
};
const WEIGHTS = Object.keys(PRICING);
const SKUS = ['0-20', '20-50', '50-100'];
function getTier(v) { return v >= 10000 ? '10k+' : v >= 5000 ? '5k-10k' : v >= 3000 ? '3k-5k' : '1k-3k'; }
const fmt0 = (n) => `$${Math.round(n).toLocaleString()}`;
const fmt2 = (n) => `$${n.toFixed(2)}`;

export default function PricingCalculator() {
  const [volume, setVolume] = useState(8500);
  const [weight, setWeight] = useState('<1lb');
  const [sku, setSku] = useState('0-20');

  const tier = useMemo(() => getTier(volume), [volume]);
  const svRate = useMemo(() => PRICING[weight]?.[sku]?.[tier] || 4.50, [weight, sku, tier]);
  const svMonthly = svRate * volume;
  const compRate = useMemo(() => {
    const base = { '<1lb': 5.50, '1-3lb': 6.50, '3-5lb': 7.50, '5-7lb': 8.50 }[weight] || 7;
    const skuExtra = { '0-20': 0.50, '20-50': 1.00, '50-100': 1.50 }[sku] || 1;
    return base + skuExtra;
  }, [weight, sku]);
  const compMonthly = compRate * volume + 900;
  const savings = compMonthly - svMonthly;
  const pct = Math.round((savings / compMonthly) * 100);
  const svBarWidth = Math.round((svMonthly / compMonthly) * 100);

  return (
    <section id="pricing" data-testid="pricing-section" className="py-14 sm:py-20 lg:py-28 bg-[#191E29]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            Discover your <span className="text-[#07c28e]">savings potential</span>
          </h2>
          <p className="text-base text-slate-400 max-w-xl mx-auto mt-3">See how much you could save by switching to Ship Velocity.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 max-w-6xl mx-auto">

          {/* ─── INPUT PANEL (2 cols) ─── */}
          <div data-testid="pricing-input-panel" className="lg:col-span-2 bg-[#0f1319] border border-white/8 rounded-2xl p-6 space-y-6">

            {/* Volume */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Monthly Orders</span>
                <span className="font-mono-data text-sm font-bold text-[#07c28e]">{volume.toLocaleString()}</span>
              </div>
              <input data-testid="calc-slider" type="range" min={1000} max={15000} step={100} value={volume} onChange={(e) => setVolume(+e.target.value)} className="w-full" />
              <div className="flex justify-between text-[10px] text-slate-600 mt-1"><span>1K</span><span>5K</span><span>10K</span><span>15K</span></div>
            </div>

            {/* Weight */}
            <div>
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-3">Avg. Package Weight</span>
              <div className="grid grid-cols-2 gap-2">
                {WEIGHTS.map((w) => (
                  <button key={w} data-testid={`weight-btn-${w}`} onClick={() => setWeight(w)}
                    className={`py-2.5 rounded-lg text-xs font-bold transition-all ${weight === w ? 'bg-[#07c28e] text-white shadow-md shadow-[#07c28e]/20' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                  >{w}</button>
                ))}
              </div>
            </div>

            {/* SKU */}
            <div>
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-3">SKU Count</span>
              <div className="grid grid-cols-3 gap-2">
                {SKUS.map((s) => (
                  <button key={s} data-testid={`sku-btn-${s}`} onClick={() => setSku(s)}
                    className={`py-2.5 rounded-lg text-xs font-bold transition-all ${sku === s ? 'bg-[#07c28e] text-white shadow-md shadow-[#07c28e]/20' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                  >{s}</button>
                ))}
              </div>
            </div>
          </div>

          {/* ─── RESULTS PANEL (3 cols) ─── */}
          <div className="lg:col-span-3 space-y-5">

            {/* Comparison bars */}
            <div data-testid="pricing-comparison" className="bg-[#0f1319] border border-white/8 rounded-2xl p-6 space-y-5">
              <div className="flex items-center gap-2 mb-1">
                <Scales size={18} weight="duotone" className="text-[#07c28e]" />
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Cost Comparison</span>
              </div>

              {/* Competitor bar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Avg. Competitor</span>
                  <span className="font-mono-data text-sm font-bold text-slate-300">{fmt0(compMonthly)}<span className="text-slate-500 font-normal">/mo</span></span>
                </div>
                <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-red-500/60 rounded-full transition-all duration-500" style={{ width: '100%' }} />
                </div>
              </div>

              {/* Ship Velocity bar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-white">Ship Velocity</span>
                  <span className="font-mono-data text-sm font-bold text-[#07c28e]">{fmt0(svMonthly)}<span className="text-slate-500 font-normal">/mo</span></span>
                </div>
                <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-[#07c28e] rounded-full transition-all duration-500" style={{ width: `${svBarWidth}%` }} />
                </div>
              </div>

              {/* Per-order comparison */}
              <div className="flex items-center justify-between pt-3 border-t border-white/5">
                <div className="text-center flex-1">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Their Rate</p>
                  <p className="font-mono-data text-lg font-bold text-slate-400 line-through">{fmt2(compRate)}</p>
                </div>
                <div className="text-slate-600 px-3">vs</div>
                <div className="text-center flex-1">
                  <p className="text-[10px] text-[#07c28e] uppercase tracking-wider mb-1">Your Rate</p>
                  <p className="font-mono-data text-lg font-bold text-[#07c28e]">{fmt2(svRate)}</p>
                </div>
                <div className="text-center flex-1 bg-[#07c28e]/10 rounded-xl py-2">
                  <p className="text-[10px] text-[#07c28e] uppercase tracking-wider mb-1">You Save</p>
                  <p className="font-mono-data text-lg font-bold text-[#07c28e]">{fmt2(compRate - svRate)}</p>
                </div>
              </div>
            </div>

            {/* Savings highlight */}
            <div data-testid="pricing-savings" className="relative overflow-hidden rounded-2xl p-6" style={{ background: 'linear-gradient(135deg, rgba(7,194,142,0.15), rgba(7,194,142,0.05))' }}>
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#07c28e]/40 to-transparent" />
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <TrendDown size={18} weight="bold" className="text-[#07c28e]" />
                    <span className="text-xs font-bold text-[#07c28e] uppercase tracking-wider">Total Savings</span>
                  </div>
                  <p className="font-mono-data text-4xl sm:text-5xl font-bold text-white">{fmt0(savings)}</p>
                  <p className="text-sm text-slate-400 mt-1">{fmt0(savings * 12)} saved per year</p>
                </div>
                <div className="flex flex-col items-center gap-1 bg-[#07c28e]/15 px-6 py-4 rounded-2xl">
                  <span className="font-mono-data text-3xl font-bold text-[#07c28e]">{pct}%</span>
                  <span className="text-[10px] text-[#07c28e] font-semibold uppercase">cheaper</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <a href="#hero" data-testid="pricing-cta"
              onClick={(e) => { e.preventDefault(); document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="flex items-center justify-center gap-2 w-full py-4 bg-[#07c28e] text-white font-bold text-base rounded-full hover:bg-[#06a879] transition-colors duration-300 shadow-lg shadow-[#07c28e]/20">
              Schedule My Call <ArrowRight size={16} weight="bold" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
